import { Box, Button, Typography } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { styled } from "@mui/material";
import { useTheme } from "@mui/material";
import { useParams } from "react-router";
import WealthAPILogo from "@components/Logo/images/wealthapi-logo.svg?react";
import WealthAPIWhiteLogo from "@components/Logo/images/wealthapi-logo-white.svg?react";
import { BankImportLayout } from "@components/common/layouts/BankImportLayout";
import { getDashboardUrl } from "@helpers/mainAppUrls";
import { useBanksId } from "@bn/api/apiv1";
import { LoadingProgressBar } from "@components/common/progress/LoadingProgressBar";

import BackgroundImage from "./background.svg?react";

const Background = styled(Box)`
  position: absolute;
  top: 0;
  right: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  display: flex;
  justify-content: flex-end;
  color: ${({ theme }) => theme.palette.background.default};
`;

const MainContainer = styled(Box)(({ theme }) => ({
  [theme.breakpoints.down(720)]: {
    borderRadius: `${theme.shape.borderRadius * 3}px`,
    display: "block !important",
    border: `1px solid ${theme.palette.border.secondary}`,
    padding: theme.spacing(6, 5),
    position: "relative",
  },
}));

const Container = styled("div")`
  max-width: 720px;
  display: flex;
  position: relative;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(4)};
  padding-top: ${({ theme }) => theme.spacing(24)};
  overflow: hidden;
  z-index: 2;
`;

export interface Props {
  onSubmit: () => void;
}

export const LegalNote = ({ onSubmit }: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { bankSlug } = useParams();
  const { data: bank, isFetching } = useBanksId({
    id: Number(bankSlug),
  });

  if (isFetching) {
    return <LoadingProgressBar />;
  }

  return (
    <BankImportLayout
      title={t("bankConnections.legalNote.pageTitle")}
      bankName={bank?.name}
      bankLogoUrl={bank?.iconUrl}
      backUrl={getDashboardUrl()}
    >
      <MainContainer>
        <Background>
          <BackgroundImage />
        </Background>
        <Container data-testid="legal-note-first-import">
          <Box position="absolute" right={theme.spacing(8.5)} top="0">
            {theme.palette.mode === "dark" ? (
              <WealthAPIWhiteLogo />
            ) : (
              <WealthAPILogo />
            )}
          </Box>
          <Typography variant="h4">
            {t("bankConnections.legalNote.title")}
          </Typography>

          <Typography variant="h6">
            {t("bankConnections.legalNote.poweredBy")}
          </Typography>

          <Typography variant="body1">
            {t("bankConnections.legalNote.finApi")}
          </Typography>

          <Typography variant="body1">
            {t("bankConnections.legalNote.dataProtection")}
          </Typography>

          <Button
            onClick={onSubmit}
            type="submit"
            color="primary"
            variant={theme.bankConnection?.button?.variant || "primary"}
            disableElevation
            data-testid="submit-button"
          >
            {t("bankConnections.legalNote.next")}
          </Button>
        </Container>
      </MainContainer>
    </BankImportLayout>
  );
};
